import { useCallback, useEffect, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
// Bundle the pdfjs worker locally so it is served same-origin (the previous
// CDN URL was blocked by CORS). pdfjs-dist is pinned to the exact version
// `react-pdf` bundles internally, since the API and worker versions must
// match.
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import IconButton, {
  IconButtonTypes,
} from '@lib/components/IconButton/IconButton';
import Typography from '@lib/components/Typography/Typography';
import downloadHandler from '@lib/utils/downloadHandler';
import isMobile from '@lib/utils/isMobile';
import useElementDimensions from '@lib/hooks/useElementDimensions';
import styles from './PDFViewer.module.scss';

// Set at module scope, not in a useEffect: <Document> kicks off loading the
// PDF from its own child effect, which runs before this component's effects
// (React fires child effects before parent effects), so a useEffect here
// would race it and the worker could still be the react-pdf default.
pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

const ZOOM_STEP = 0.2;
const MIN_ZOOM = 0.4;
const MAX_ZOOM = 4;

interface Props {
  fileUrl: string;
  downloadUrl?: string;
  onFullScreenToggle?: () => void;
  isFullScreen?: boolean;
  useDownloadTool?: boolean;
  useFavoriteTool?: boolean;
  onFavoriteToggle?: () => void;
  isFavorited?: boolean;
  isFavoriteLoading?: boolean;
}

// Prints the raw PDF via the browser's native PDF viewer instead of the
// rendered canvas. Unlike ImageViewer's print (a hidden iframe holding a
// plain `document.write`-generated HTML page, which prints fine via
// `contentWindow.print()`), a PDF's rendering is handed off to Chrome's
// built-in PDF viewer plugin, and *that* doesn't honor `contentWindow.print()`
// from any ancestor frame — confirmed by direct testing across a same-origin
// `blob:` src, every size/timing, and even a plain host document with the PDF
// nested inside it via <embed> one level down. Only a genuine top-level
// `window.print()` reliably opens the dialog, so the PDF has to actually be
// the tab's own document — hence a new tab instead of an iframe.
//
// The tab is opened synchronously (before the `fetch`) so the click that
// triggered this still counts as the user gesture `window.open` needs —
// opening it only after the async fetch resolves gets silently popup-blocked.
function printFile(fileUrl: string) {
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    console.warn('printFile: print window was blocked');
    return;
  }

  (async () => {
    let objectUrl: string;
    try {
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      objectUrl = URL.createObjectURL(blob);
    } catch (error) {
      console.warn(error);
      printWindow.close();
      return;
    }

    printWindow.addEventListener('load', () => {
      printWindow.focus();
      printWindow.print();
    });
    printWindow.addEventListener('unload', () => {
      URL.revokeObjectURL(objectUrl);
    });
    printWindow.location.href = objectUrl;
  })();
}

export default function PDFViewer(props: Props) {
  const {
    fileUrl,
    downloadUrl,
    onFullScreenToggle,
    isFullScreen,
    useDownloadTool = true,
    useFavoriteTool = false,
    onFavoriteToggle,
    isFavorited,
    isFavoriteLoading,
  } = props;
  const [isLoading, setIsLoading] = useState(true);
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [zoom, setZoom] = useState(1);
  // Pages get mounted (and their canvas rendered) once they scroll near the
  // viewport, not all at once — a multi-hundred-page document would otherwise
  // render every canvas up front, stalling the browser and leaving newly
  // navigated-to pages blank while still mid-render. Never removed once
  // added, so already-rendered pages don't disappear as the user scrolls on.
  const [loadedPages, setLoadedPages] = useState<Set<number>>(
    () => new Set([1]),
  );
  // Rendered height of a loaded page, reused as the placeholder height for
  // not-yet-loaded pages so the scrollbar/scroll position stay stable instead
  // of jumping around as pages lazily mount.
  const [pageHeight, setPageHeight] = useState<number | null>(null);
  const { phone, tablet } = isMobile();
  const isFitToWidth = phone || tablet;

  const viewerWrapRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef<(HTMLDivElement | null)[]>([]);
  const { dimensions } = useElementDimensions(
    viewerWrapRef,
    String(isFullScreen),
  );

  useEffect(() => {
    setPageNumber(1);
    setZoom(1);
    setLoadedPages(new Set([1]));
    setPageHeight(null);
  }, [fileUrl]);

  // Reset to the fit/default zoom whenever the viewer enters or exits
  // fullscreen, mirroring the previous zoomTo(defaultScale) behaviour.
  useEffect(() => {
    setZoom(1);
  }, [isFullScreen]);

  const handleDocumentLoadSuccess = useCallback((pdf: { numPages: number }) => {
    setNumPages(pdf.numPages);
    setIsLoading(false);
  }, []);

  // All pages render at once in a scrollable container (see below), so the
  // "current page" is whichever page is most visible in the viewport, not a
  // single rendered page. Track it with an observer instead of state driving
  // which page renders.
  //
  // The observer only triggers a recompute here — picking the page itself is
  // done by scanning `pageRefs` geometrically rather than trusting whichever
  // entries the observer happens to hand back. Picking by highest
  // `intersectionRatio` breaks whenever the container is tall enough to show
  // several pages fully at once (e.g. fullscreen mode): they all tie at 1.0,
  // and which one "wins" depends on unspecified entry ordering — that's what
  // produced the wrong initial page number in fullscreen. Scanning in page
  // order for the last page whose top has crossed the container's midline is
  // deterministic regardless of how many pages fit in view at once.
  useEffect(() => {
    const container = viewerWrapRef.current;
    if (!container || numPages === 0) return undefined;

    const updateCurrentPage = () => {
      const containerRect = container.getBoundingClientRect();
      const midline = containerRect.top + containerRect.height / 2;
      let current = 1;
      for (let i = 0; i < pageRefs.current.length; i += 1) {
        const page = pageRefs.current[i];
        if (!page) break;
        if (page.getBoundingClientRect().top > midline) break;
        current = i + 1;
      }
      setPageNumber(current);
    };

    const observer = new IntersectionObserver(updateCurrentPage, {
      root: container,
      threshold: [0.25, 0.5, 0.75],
    });
    pageRefs.current.forEach((page) => page && observer.observe(page));

    return () => observer.disconnect();
  }, [numPages, fileUrl]);

  // Separate observer, with a generous rootMargin, drives which pages are
  // mounted at all (see `loadedPages` above) — deliberately loose so pages
  // are ready just before they're scrolled to, unlike the tight thresholds
  // the "current page" observer above needs for an accurate counter.
  useEffect(() => {
    const container = viewerWrapRef.current;
    if (!container || numPages === 0) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const pagesToLoad = entries
          .filter((entry) => entry.isIntersecting)
          .map(
            (entry) =>
              pageRefs.current.indexOf(entry.target as HTMLDivElement) + 1,
          )
          .filter((page) => page > 0);
        if (pagesToLoad.length === 0) return;
        setLoadedPages((prev) => {
          if (pagesToLoad.every((page) => prev.has(page))) return prev;
          const next = new Set(prev);
          pagesToLoad.forEach((page) => next.add(page));
          return next;
        });
      },
      { root: container, rootMargin: '100% 0px', threshold: 0 },
    );
    pageRefs.current.forEach((page) => page && observer.observe(page));

    return () => observer.disconnect();
  }, [numPages, fileUrl]);

  // `pageNumber` state lags behind a click: it's only corrected once the
  // smooth-scroll finishes and the IntersectionObserver above reports back,
  // which is asynchronous. Reading it in the click handlers would make a
  // second click (before that round-trip completes) re-target the same page
  // instead of advancing. This ref is updated synchronously on every click
  // and kept in sync with the observer's updates, so consecutive clicks
  // always advance from the last requested page.
  const currentPageRef = useRef(1);
  useEffect(() => {
    currentPageRef.current = pageNumber;
  }, [pageNumber]);

  const scrollToPage = (page: number) => {
    currentPageRef.current = page;
    setPageNumber(page);
    pageRefs.current[page - 1]?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };
  const goToPreviousPage = () =>
    scrollToPage(Math.max(1, currentPageRef.current - 1));
  const goToNextPage = () =>
    scrollToPage(Math.min(numPages, currentPageRef.current + 1));
  const zoomIn = () =>
    setZoom((scale) => Math.min(MAX_ZOOM, scale + ZOOM_STEP));
  const zoomOut = () =>
    setZoom((scale) => Math.max(MIN_ZOOM, scale - ZOOM_STEP));

  return (
    <div className={styles.root}>
      <div className={styles.toolbar}>
        <div className={styles.actionsRow}>
          <div className={styles.actionsRow}>
            <IconButton
              icon="navigate_before"
              onClick={goToPreviousPage}
              symbolsOutlined
              disabled={isLoading || pageNumber <= 1}
            />
            <Typography variant="body">
              {isLoading ? 0 : pageNumber} / {numPages}
            </Typography>
            <IconButton
              icon="navigate_next"
              onClick={goToNextPage}
              symbolsOutlined
              disabled={isLoading || pageNumber >= numPages}
            />
          </div>
          {onFullScreenToggle && (
            <IconButton
              icon={isFullScreen ? 'fullscreen_exit' : 'fullscreen'}
              onClick={onFullScreenToggle}
              symbolsOutlined
              disabled={isLoading}
            />
          )}
          <IconButton
            icon="zoom_in"
            onClick={zoomIn}
            symbolsOutlined
            disabled={isLoading}
          />
          <IconButton
            icon="zoom_out"
            onClick={zoomOut}
            symbolsOutlined
            disabled={isLoading}
          />
        </div>
        <div className={styles.actionsRow}>
          <IconButton
            icon="print"
            onClick={() => {
              printFile(fileUrl);
            }}
            symbolsOutlined
            disabled={isLoading}
          />
          {useDownloadTool && (
            <IconButton
              icon="download"
              onClick={() => downloadHandler(downloadUrl)}
              symbolsOutlined
              disabled={isLoading}
            />
          )}
          {useFavoriteTool && onFavoriteToggle && (
            <IconButton
              icon={isFavorited ? 'bookmark_star' : 'bookmark'}
              onClick={onFavoriteToggle}
              symbolsOutlined
              filled={isFavorited}
              isLoading={isFavoriteLoading}
              type={IconButtonTypes.PrimaryOutlined}
              disabled={isLoading}
            />
          )}
        </div>
      </div>
      <div
        ref={viewerWrapRef}
        className={isFullScreen ? styles.viewerWrapFull : styles.viewerWrap}
      >
        <Document
          file={fileUrl}
          onLoadSuccess={handleDocumentLoadSuccess}
          loading=""
          className={styles.document}
        >
          {Array.from({ length: numPages }, (_, index) => {
            const page = index + 1;
            const isPageLoaded = loadedPages.has(page);
            return (
              <div
                key={page}
                className={styles.pageWrap}
                style={
                  !isPageLoaded && pageHeight
                    ? { height: pageHeight }
                    : undefined
                }
                ref={(el) => {
                  pageRefs.current[index] = el;
                }}
              >
                {isPageLoaded && (
                  <Page
                    pageNumber={page}
                    scale={zoom}
                    width={
                      isFitToWidth && dimensions?.width
                        ? dimensions.width
                        : undefined
                    }
                    onLoadSuccess={(loadedPage) =>
                      setPageHeight(loadedPage.height)
                    }
                  />
                )}
              </div>
            );
          })}
        </Document>
      </div>
    </div>
  );
}
